import Axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';

const STORAGE_PREFIX = 'flayr:quoteFavorites:';
const FAVORITES_CHANGED = 'flayr:quoteFavoritesChanged';

export type QuoteFavoriteEntry = {
  profileId: string;
  photo?: string;
  name?: string;
};

function parseFavorites(raw: string | null): QuoteFavoriteEntry[] {
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as unknown;
    if (!Array.isArray(data) || data.length === 0) return [];
    if (typeof data[0] === 'string') {
      return (data as string[]).map((profileId) => ({ profileId }));
    }
    return (data as QuoteFavoriteEntry[]).filter(
      (x) => x && typeof x.profileId === 'string'
    );
  } catch {
    return [];
  }
}

function broadcastChange(quoteId: string) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(FAVORITES_CHANGED, { detail: { quoteId } })
  );
}

function persistFavorites(
  quoteId: string,
  favorites: QuoteFavoriteEntry[]
) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${quoteId}`, JSON.stringify(favorites));
    broadcastChange(quoteId);
  } catch {
    /* ignore quota / private mode */
  }

  // Best-effort server sync so shortlist persists across devices/sessions.
  void Axios.patch(`${API_URL}/v1/quotes/${quoteId}/shortlist`, {
    shortlist: favorites,
  }).catch(() => {
    /* keep local behavior even if API sync fails */
  });
}

/**
 * Client-side favorites for quote marketplace cards until a server API exists.
 * Keyed by quote id; persisted in localStorage. Multiple hook instances for the
 * same quote stay in sync via a window event after toggles.
 */
export function useQuoteFavoriteProviders(quoteId: string | undefined) {
  const [favorites, setFavorites] = useState<QuoteFavoriteEntry[]>([]);

  useEffect(() => {
    if (!quoteId || typeof window === 'undefined') {
      setFavorites([]);
      return;
    }

    const load = () => {
      try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${quoteId}`);
        setFavorites(parseFavorites(raw));
      } catch {
        setFavorites([]);
      }
    };

    load();

    const onExternalChange = (e: Event) => {
      const detail = (e as CustomEvent<{ quoteId: string }>).detail;
      if (detail?.quoteId === quoteId) load();
    };

    window.addEventListener(FAVORITES_CHANGED, onExternalChange);
    return () =>
      window.removeEventListener(FAVORITES_CHANGED, onExternalChange);
  }, [quoteId]);

  const isFavorite = useCallback(
    (profileId: string) =>
      favorites.some((e) => e.profileId === profileId),
    [favorites]
  );

  const toggleFavorite = useCallback(
    (
      profileId: string,
      meta?: { photo?: string; name?: string }
    ) => {
      setFavorites((prev) => {
        const has = prev.some((e) => e.profileId === profileId);
        const next = has
          ? prev.filter((e) => e.profileId !== profileId)
          : [
              ...prev,
              {
                profileId,
                photo: meta?.photo,
                name: meta?.name,
              },
            ];
        if (quoteId && typeof window !== 'undefined') {
          persistFavorites(quoteId, next);
        }
        return next;
      });
    },
    [quoteId]
  );

  const reorderFavorites = useCallback(
    (orderedProfileIds: string[]) => {
      setFavorites((prev) => {
        if (!quoteId || !orderedProfileIds.length) return prev;

        const byId = new Map(prev.map((entry) => [entry.profileId, entry]));
        const used = new Set<string>();
        const next: QuoteFavoriteEntry[] = [];

        orderedProfileIds.forEach((profileId) => {
          const entry = byId.get(profileId);
          if (entry && !used.has(profileId)) {
            used.add(profileId);
            next.push(entry);
          }
        });

        // Keep any unknown/missing IDs at the end to avoid accidental drops.
        prev.forEach((entry) => {
          if (!used.has(entry.profileId)) {
            next.push(entry);
          }
        });

        if (quoteId && typeof window !== 'undefined') {
          persistFavorites(quoteId, next);
        }

        return next;
      });
    },
    [quoteId]
  );

  return { favorites, isFavorite, toggleFavorite, reorderFavorites };
}
